import {Color} from '@benchling/nucleus-ui';
import {Divider, Title, Table, Stack} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {MoleculeFormDetailsQuery} from 'chemistry/molecules/tabs/__generated/MoleculeFormDetailsQuery';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {BioentityChip} from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';

import {TEXT_FIELD_BACKGROUND_COLOR, TEXT_FIELD_BORDER} from '../MoleculeConstants';

export const MOLECULE_FORM_DETAILS_QUERY = gql`
  query MoleculeFormDetailsQuery($moleculeId: StrictString!, $moleculeVersionId: StrictString!)
  @skipAudit
  @engComponent(name: CHEMISTRY) {
    molecule(id: $moleculeId) {
      ... on MoleculeNode {
        id
        version(versionId: $moleculeVersionId) {
          id
          chemicalStructure {
            id
            salts {
              ratioNumerator
              ratioDenominator
              salt {
                id
                name
              }
            }
            solvates {
              ratioNumerator
              ratioDenominator
              solvate {
                id
                name
              }
            }
          }
        }
        compoundMoleculeLink {
          ... on MoleculeNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface MoleculeFormDetailsProps {
  moleculeId: string;
  moleculeVersionId: string;
}

export default function MoleculeFormDetails(props: MoleculeFormDetailsProps): JSX.Element | null {
  if (!BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS) {
    return null;
  }

  return (
    <BenchlingQuery
      query={MOLECULE_FORM_DETAILS_QUERY}
      variables={{moleculeId: props.moleculeId, moleculeVersionId: props.moleculeVersionId}}
      renderLoading={() => <></>}
    >
      {(data: MoleculeFormDetailsQuery) => {
        if (isInaccessible(data.molecule) || !data.molecule.version) {
          return <></>;
        }

        return (
          <>
            <Divider mt="lg" />
            <Title
              transform="uppercase"
              color={Color.GRAY_600}
              order={2}
              weight={FONT_WEIGHT_PROXIMA_SEMIBOLD}
              pt="lg"
            >
              Components
            </Title>
            <Stack>
              <div>
                <div className={css(styles.fieldHeader)}>Base Compound</div>
                <div className={css(styles.fieldValue)}>
                  {data.molecule.compoundMoleculeLink == null ? (
                    'No base compound'
                  ) : (
                    <BioentityChip
                      id={
                        isInaccessible(data.molecule.compoundMoleculeLink)
                          ? data.molecule.compoundMoleculeLink.invalidId
                          : data.molecule.compoundMoleculeLink.id
                      }
                      size={ChipSize.TABLE_CELL}
                      validationExtras={null}
                    />
                  )}
                </div>
              </div>
              <Table withBorder={true} withColumnBorders={true} bg={TEXT_FIELD_BACKGROUND_COLOR}>
                <thead>
                  <tr>
                    <th>Salt or Solvate</th>
                    <th>Stoichiometric Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {data.molecule.version.chemicalStructure.salts.map((counterpart) => (
                    <tr key={counterpart.salt.id}>
                      <td>{counterpart.salt.name}</td>
                      <td>
                        {counterpart.ratioNumerator}:{counterpart.ratioDenominator}
                      </td>
                    </tr>
                  ))}
                  {data.molecule.version.chemicalStructure.solvates.map((counterpart) => (
                    <tr key={counterpart.solvate.id}>
                      <td>{counterpart.solvate.name}</td>
                      <td>
                        {counterpart.ratioNumerator}:{counterpart.ratioDenominator}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Stack>
          </>
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  fieldHeader: {
    fontWeight: 'bold',
    margin: '4px 0px',
    paddingTop: '10px',
  },
  fieldValue: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '6px 12px',
    background: TEXT_FIELD_BACKGROUND_COLOR,
    border: TEXT_FIELD_BORDER,
    boxSizing: 'border-box',
    borderRadius: '4px',
    overflowWrap: 'anywhere',
  },
});
